import React, { useRef, useState, useEffect } from 'react';

const AudioPlayer = ({ audioUrl }) => {
  const audioRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const progressBarRef = useRef(null);

  // Create audio element on component mount
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      
      // Set up event listeners
      audioRef.current.onloadeddata = () => {
        setIsLoaded(true);
      };
      
      audioRef.current.oncanplay = () => {
        audioRef.current.play();
        setIsPlaying(true);
      };
      
      audioRef.current.ontimeupdate = handleTimeUpdate;
      audioRef.current.onended = handleEnded;
    }
    
    // Clean up on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, []);

  // Update audio source when audioUrl changes
  useEffect(() => {
    if (audioRef.current && audioUrl) {
      // Reset states
      setIsLoaded(false);
      setIsPlaying(false);
      setProgress(0);
      
      // Set the audio source
      audioRef.current.src = audioUrl;
      audioRef.current.load();
    }
  }, [audioUrl]);

  const handlePlayPause = () => {
    if (!audioRef.current || !isLoaded) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current && !isDragging) {
      const currentProgress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(currentProgress);
    }
  };

  const handleProgressBarClick = (e) => {
    if (!audioRef.current || !isLoaded || !progressBarRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    const seekPosition = position * 100;
    
    audioRef.current.currentTime = (seekPosition / 100) * audioRef.current.duration;
    setProgress(seekPosition);
  };
  
  const handleProgressBarDrag = (e) => {
    if (!audioRef.current || !isLoaded || !progressBarRef.current) return;
    
    const rect = progressBarRef.current.getBoundingClientRect();
    let position = (e.clientX - rect.left) / rect.width;
    
    // Clamp the position between 0 and 1
    position = Math.max(0, Math.min(position, 1));
    
    const newProgress = position * 100;
    setProgress(newProgress);
    
    // Update audio time if we're dragging
    if (isDragging) {
      audioRef.current.currentTime = position * audioRef.current.duration;
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
  };
  
  const handleOpenInNewTab = () => {
    if (!audioRef.current || !audioRef.current.src) return;
    window.open(audioRef.current.src, '_blank');
  };

  return (
    <div className="w-full p-2">
      <div className="flex items-center justify-between">
        {isLoaded ? (
          <>
            {/* Play/Pause Button */}
            <button 
              onClick={handlePlayPause}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white hover:bg-gray-800 hover:scale-110 active:scale-95 transition-all duration-150 flex-shrink-0"
            >
              {isPlaying ? (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="4" width="4" height="16" />
                  <rect x="14" y="4" width="4" height="16" />
                </svg>
              ) : (
                <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <polygon points="5,3 19,12 5,21" />
                </svg>
              )}
            </button>
            
            {/* Custom Progress Bar with draggable functionality */}
            <div 
              className="mx-3 flex-grow h-8 flex items-center relative cursor-pointer"
              onClick={handleProgressBarClick}
              onMouseDown={(e) => {
                setIsDragging(true);
                handleProgressBarDrag(e);
              }}
              onMouseMove={(e) => isDragging && handleProgressBarDrag(e)}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
              ref={progressBarRef}
            >
              {/* Background track */}
              <div className="w-full bg-gray-200 h-2 rounded-full absolute">
                {/* Progress indicator */}
                <div 
                  className="bg-black h-2 rounded-full relative" 
                  style={{ width: `${progress}%` }}
                >
                  {/* Draggable handle */}
                  <div 
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2 w-4 h-4 bg-black rounded-full hover:scale-110 transition-transform duration-150 border-2 border-white"
                  />
                </div>
              </div>
            </div>
            
            {/* Open in New Tab Button */}
            <button
              onClick={handleOpenInNewTab}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white hover:bg-gray-800 hover:scale-110 active:scale-95 transition-all duration-150 flex-shrink-0"
              title="Open in new tab"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 19H5V5h7V3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
              </svg>
            </button>
          </>
        ) : (
          // Show nothing if audio isn't loaded yet
          <div className="flex-grow"></div> 
        )}
      </div>
    </div>
  );
};

export default AudioPlayer;